import React from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import moment from "moment";

import { ReportOperatorSalary } from "../../../models/ReportOperatorSalary";
import axiosClient from "../../../config/axios";

import OperatorSalaryForm from "../../../forms/OperatorSalaryForm";
import ReportOperatorSalarySchema from "../../../validations/ReportOperatorSalarySchema";
import TableOperatorSalary from "../../../components/reports/TableOperatorSalary";
import Loader from "../../../components/loader";
import { toCamelCase } from "../../../utils";

const OperatorSalary = () => {
  const [loading, setLoading] = React.useState(true);
  const [records, setRecords] = React.useState([]);
  const [orders, setOrders] = React.useState([]);
  const [operators, setOperators] = React.useState([]);
  const [status, setStatus] = React.useState([]);
  const [serviceTypes, setServiceTypes] = React.useState([]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: ReportOperatorSalary(),
    validationSchema: ReportOperatorSalarySchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const adjustedStart = moment(values.start).format("YYYY-MM-DD");
        const adjustedEnd = moment(values.end).format("YYYY-MM-DD");

        const endpointNormal = `/reports/operator-salary?start=${adjustedStart}&end=${adjustedEnd}&status=${values.status}&id_operador=${values.operator}`;
        const { data: normalData } = await axiosClient.get(endpointNormal);

        const dataTable = [];

        normalData?.forEach((record) => {
          const { operador } = record;
          record.orders.forEach((order) => {
            const { Order } = order;
            dataTable.push({
              ...Order,
              subtotal: Order.orden_en_cero ? 0 : Order.subtotal,
              operador,
              totalDistance: order.totalDistance,
              tipo: "Normal",
            });
          });
        });

        // Obtener órdenes viales
        const statusParam = values.status && values.status !== "all" ? `&status=${values.status}` : "";
        const endpointVial = `/order-vial/datatable?draw=1&start=0&length=10000&dateFrom=${adjustedStart}&dateTo=${adjustedEnd}${statusParam}`;

        const { data: vialData } = await axiosClient.get(endpointVial);

        vialData.data.forEach((vialOrder) => {
          const operador = vialOrder.operator;

          dataTable.push({
            id: vialOrder.id,
            folio: vialOrder.folio,
            subtotal: (vialOrder.total / 1.16).toFixed(2),
            fecha_hora_reporte: vialOrder.fecha_hora_orden,
            grua: vialOrder.grua,
            operador: operador,
            Crane: vialOrder.Crane,
            tipoServicio: vialOrder?.typeService?.type_service_name,
            tipo: "Varo Express",
            razon: `Varo Express: ${vialOrder?.typeService?.type_service_name || ""}`,
            comisionPorGrua: 10,
          });
        });

        // Ordenar por fecha (de más antigua a más reciente)
        dataTable.sort((a, b) => new Date(a.fecha_hora_reporte) - new Date(b.fecha_hora_reporte));

        //setRecords(normalData);
        setOrders(dataTable);
        // Agrupar órdenes viales por operador
        const vialGrouped = {};
        vialData.data.forEach((vialOrder) => {
          const operador = vialOrder.operator;
          const operadorId = operador?.id;

          if (!operadorId) return;

          if (!vialGrouped[operadorId]) {
            vialGrouped[operadorId] = {
              operador,
              orders: [],
            };
          }

          vialGrouped[operadorId].orders.push({
            Order: {
              id: vialOrder.id,
              folio: vialOrder.folio,
              subtotal: (vialOrder.total / 1.16).toFixed(2),
              fecha_hora_reporte: vialOrder.fecha_hora_orden,
              grua: vialOrder.grua,
              Crane: vialOrder.Crane,
              tipoServicio: vialOrder?.typeService?.type_service_name,
              tipo: "Varo Express",
              razon: `Varo Express: ${vialOrder?.typeService?.type_service_name || ""}`,
              comisionPorGrua: 10,
            },
            totalDistance: 0,
            
          });
        });

        // Combinar los operadores normales y los viales
        const combinedRecords = [...normalData];

        // Añadir operadores viales que no estén ya
        Object.values(vialGrouped).forEach((vialOperator) => {
          const index = combinedRecords.findIndex(
            (r) => r.operador?.id === vialOperator.operador?.id
          );

          if (index >= 0) {
            combinedRecords[index].orders = [
              ...(combinedRecords[index].orders || []),
              ...vialOperator.orders,
            ];
          } else {
            combinedRecords.push(vialOperator);
          }
        });
      
        // Ordenar cada grupo de órdenes por fecha (de más antigua a más reciente)
        combinedRecords.forEach((record) => {
          record.orders?.sort((a, b) => {
            const fechaA = new Date(a.Order?.fecha_hora_reporte);
            const fechaB = new Date(b.Order?.fecha_hora_reporte);
            return fechaA - fechaB;
          });
        });

        setRecords(combinedRecords);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchData = async () => {
    try {
        const response = await axiosClient.get('/users/allOperators-active');
        
        const formattedOptions = [
            { value: "all", label: "Todas las opciones" }, 
            ...response.data.data.map(operator => ({
                value: operator.id,
                label: toCamelCase(operator.name + ' ' + operator.first_surname)
            }))
        ];
        
        setOperators(formattedOptions);

        const responseStatus = await axiosClient.get('/orderStatuses/allWithOrderCount');
        
        const formattedOptionsStatus = [
            { value: "all", label: "Todas las opciones" }, 
            ...responseStatus.data.data.map(operator => ({
                value: operator.id,
                label: operator.order_status
            }))
        ];
        
        setStatus(formattedOptionsStatus);

        // Obtener tipos de servicio desde el endpoint
        const { data: typeServicesResponse } = await axiosClient.get('/typeServices/datatable');
        setServiceTypes(
          typeServicesResponse.data.map((type) => ({
            value: type.id,
            label: type.type_service_name,
          }))
        );

    } catch (error) {
        console.error(error);
    }
  };

 React.useEffect(() => {
    const loadAndSubmit = async () => {
      await fetchData();        
      formik.handleSubmit();     
    };
    loadAndSubmit();
  }, []);

  return (
    <React.Fragment>
      <Loader isLoading={loading} />
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Inicio</Link>
            </li>
            <li className="breadcrumb-item active">Reporte de comisiones</li>
          </ol>
          <h1 className="page-header mb-0">Reporte de comisiones</h1>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-xl-12 card border-0 p-3">
          <OperatorSalaryForm formik={formik} records={records}  status={status} operators={operators} />
        </div>
      </div>
      <div className="row">
        <div className="col-xl-12 card border-0 p-3">
          <TableOperatorSalary
            orders={orders}
            start={formik.values.start}
            end={formik.values.end}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default OperatorSalary;
